<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            Most bad 360 photos are not caused by poor equipment or bad apps —
            they're caused by a small set of consistent technique errors that
            are entirely preventable once you know what they are. After
            analysing thousands of virtual tours created on the Travvir
            platform, here are the 12 most common 360 photography mistakes and
            exactly how to fix or avoid each one.
          </p>
        </section>

        <section>
          <h2>Camera Movement and Stability Mistakes</h2>

          <h3>Mistake 1: Rotating Too Fast</h3>
          <p>
            <strong>What it causes:</strong> Blurry frames, stitching seam
            errors, and gaps in coverage that appear as black patches or
            obviously misaligned areas in the final image.
          </p>
          <p>
            <strong>The fix:</strong> Rotate at approximately one frame every
            2–3 seconds, moving approximately 15° per step. Let the app's
            capture overlay confirm each zone before rotating to the next. If
            you're using guided capture, don't advance until the app indicator
            turns green.
          </p>

          <h3>Mistake 2: Handheld Capture Without Support</h3>
          <p>
            <strong>What it causes:</strong> Camera shake during individual
            frame captures, inconsistent height across frames, and parallax
            errors from non-centred rotation around the lens.
          </p>
          <p>
            <strong>The fix:</strong> Always use a tripod for business and
            commercial virtual tours. For casual personal use, stabilise against
            a wall or use a phone gimbal. A £15 tripod with phone mount
            completely eliminates this category of error.
          </p>

          <h3>Mistake 3: Wrong Rotation Centre</h3>
          <p>
            <strong>What it causes:</strong> Parallax error — foreground objects
            appear duplicated or "split" at stitch lines because the camera's
            perspective shifted during rotation rather than rotating around a
            fixed point.
          </p>
          <p>
            <strong>The fix:</strong> Rotate around the camera lens, not around
            the tripod or your body. Position the phone on the tripod so the
            lens is as close to the rotation axis as possible. Many tripod heads
            have a nodal point adjustment for this purpose.
          </p>
        </section>

        <section>
          <h2>Exposure and Lighting Mistakes</h2>

          <h3>Mistake 4: Auto-Exposure Changes Between Frames</h3>
          <p>
            <strong>What it causes:</strong> Visible horizontal or radial
            exposure banding in the stitched image — different sections of the
            panorama have noticeably different brightness levels, creating an
            obvious seam at every stitch line.
          </p>
          <p>
            <strong>The fix:</strong> Lock exposure before starting the capture
            sequence. On iPhone, tap and hold to lock AE/AF lock. On Android,
            switch to Pro/Manual mode and set a fixed ISO (100–200 for well-lit
            interiors) and shutter speed (1/60s for standard lighting). Never
            let auto-exposure run during a 360 capture.
          </p>

          <h3>Mistake 5: Shooting Toward Windows in Bright Daylight</h3>
          <p>
            <strong>What it causes:</strong> Extreme overexposure on window
            sections (blown out to pure white) while interior areas are
            underexposed, creating an image that shows neither the interior nor
            the view well.
          </p>
          <p>
            <strong>The fix:</strong> Position your tripod so you're shooting
            away from the brightest windows, not toward them. Close blinds or
            diffuse direct sunlight with net curtains before shooting. If you
            must include window views, shoot at dawn or dusk when the interior
            and exterior light balance more naturally.
          </p>

          <h3>Mistake 6: Mixed Colour Temperature Lighting</h3>
          <p>
            <strong>What it causes:</strong> Different sections of the 360 image
            have noticeably different colour casts — warm orange tones from
            incandescent lamps versus cool blue-white from fluorescent or
            natural light, creating jarring transitions across the stitched
            sphere.
          </p>
          <p>
            <strong>The fix:</strong> Before shooting, replace or turn off
            mixed-temperature lights. Use only one type: all LED, all
            fluorescent, or all natural light. In spaces where light temperature
            is mixed and unchangeable, lock white balance in the Pro camera mode
            to a fixed value (approximately 4000K for mixed indoor spaces).
          </p>
        </section>

        <section>
          <h2>Composition and Positioning Mistakes</h2>

          <h3>Mistake 7: Shooting at the Wrong Height</h3>
          <p>
            <strong>What it causes:</strong> Distorted perspective — shooting
            too low makes spaces appear cramped and squat; shooting too high
            looks unnatural and creates ceiling-heavy compositions.
          </p>
          <p>
            <strong>The fix:</strong> Set the camera at 1.5–1.7 metres height
            (adult standing eye level). This is the perspective that viewers
            intuitively recognise as natural and creates the most flattering
            representation of interior spaces.
          </p>

          <h3>Mistake 8: Shooting Too Close to Walls</h3>
          <p>
            <strong>What it causes:</strong> Claustrophobic compositions where
            one wall dominates the image and the room appears much smaller than
            it is. The stitching also struggles with very close objects because
            parallax error is amplified at short distances.
          </p>
          <p>
            <strong>The fix:</strong> Position the tripod in the centre of the
            room or the optimal viewing position — ideally equidistant from all
            walls. The natural vantage point from the centre is also the most
            useful for viewers navigating the tour.
          </p>

          <h3>
            Mistake 9: Including the Photographer or Equipment in the Shot
          </h3>
          <p>
            <strong>What it causes:</strong> The camera captures the full 360°,
            including behind and below the camera position. Photographers who
            don't account for their own reflection in mirrors, shadows, or the
            tripod and phone appearing in the nadir area produce images that
            look unprofessional.
          </p>
          <p>
            <strong>The fix:</strong> Use a remote shutter trigger or the app's
            timer function and move out of shot before capture. Cover or remove
            mirrors during shooting if your reflection is unavoidable. Use the
            nadir fill/logo feature in the app to cover the floor/tripod area in
            the final image.
          </p>
        </section>

        <section>
          <h2>Pre-Production and Preparation Mistakes</h2>

          <h3>Mistake 10: Not Staging the Space</h3>
          <p>
            <strong>What it causes:</strong> Because 360 photos capture the
            entire environment, any clutter, mess, or staging issues are highly
            visible from all angles. Issues that could be cropped out of a
            standard photo appear clearly in 360.
          </p>
          <p>
            <strong>The fix:</strong> Stage the space more carefully than you
            would for standard photography. Remove all visible clutter
            (including power cables, rubbish bins, coats, bags). Straighten
            furniture, fluff cushions, set tables. Do a 360° visual scan of the
            space before shooting — stand in the camera position and spin
            slowly, looking for anything that should be removed.
          </p>

          <h3>Mistake 11: Forgetting to Check for Obstacles in the Nadir</h3>
          <p>
            <strong>What it causes:</strong> The tripod and phone mount appear
            in the floor section of the image. Without nadir fill or correction,
            the finished tour has an obvious equipment intrusion at the bottom
            of every scene.
          </p>
          <p>
            <strong>The fix:</strong> All modern 360 apps (including Travvir)
            provide automatic nadir fill that covers the camera position with a
            logo or blur. Ensure this feature is enabled in your app settings
            before shooting.
          </p>

          <h3>Mistake 12: Publishing Without Quality Review</h3>
          <p>
            <strong>What it causes:</strong> Stitching errors, poor exposure,
            and composition problems that were visible in the source images
            appear in the published tour — reducing viewer trust and the
            professional impression of the content.
          </p>
          <p>
            <strong>The fix:</strong> Always review every 360 image in the
            viewer before publishing. Pan through the full sphere, check stitch
            lines, verify exposure consistency, and look for any objects or
            people that should have been removed. Reshoot any position with
            obvious quality issues.
          </p>
        </section>

        <section>
          <h2>Conclusion: Quality Control Is a Habit, Not an Afterthought</h2>
          <p>
            Every mistake on this list is preventable with the right preparation
            and habit of mind before and during the capture session. The
            photographers who consistently produce excellent 360 tours are not
            those with the best equipment — they're those who develop a
            systematic pre-shoot checklist and execute it consistently on every
            job.
          </p>
          <p>
            Build your own version of this checklist incorporating the fixes
            above, run through it at each new shooting position, and review
            every image before leaving the location. Within a few sessions,
            avoiding these mistakes becomes second nature.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a href="/blog/en/360-virtual-tour-mistakes">Virtual Tour Mistakes to Avoid</a></li>
          <li><a href="/blog/en/fix-blurry-360-photos">Fix Blurry 360 Photos</a></li>
          <li><a href="/blog/en/interior-photography-360-virtual-tour">Interior Photography Techniques</a></li>
          <li><a href="/blog/en/what-is-a-360-photo">Understanding 360 Photos</a></li>
          <li><a href="/blog/en/shoot-360-photos-with-phone">Shooting 360 Photos Correctly</a></li>
          <li><a href="/blog/en/360-virtual-tour-image-size">Optimal Image Resolution</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "360-photography-mistakes-to-avoid",
);

useBlogSeo({
  title: "12 Common 360 Photography Mistakes to Avoid [2026] | Travvir",
  description:
    "Avoid these 12 common 360 photography mistakes that ruin virtual tours. Each mistake includes a quick fix you can apply today.",
  keywords:
    "360 photography mistakes, bad virtual tour examples, 360 photo errors, virtual tour mistakes, 360 photography tips",
  slug: "360-photography-mistakes-to-avoid",
  image: postData?.image,
  postData,
  lang: "en",
});

</script>
